import { i18n } from 'boot/i18n';
import { Timestamp } from 'firebase/firestore';
import { filterPropertyTypes } from 'src/boot/globals'; // @todo Move to constants
import { computed } from 'vue';
import { useTime } from 'src/composables/time';

// eslint-disable-next-line import/prefer-default-export
export function useDateRanges(scopes = ['past', 'present', 'future', 'all'], now = new Date()) {
  const { startOfDate, subtractFromDate, addToDate, getDayOfWeek, endOfDate } = useTime();
  const nowStart = startOfDate(now, 'day');
  const nowEnd = endOfDate(now, 'day');

  const allPeriodPresets = [
    {
      labelKey: 'insights.chartConfigurator.periodPresetToday',
      id: 'today',
      fromTimestamp: nowStart,
      toTimestamp: nowEnd,
      scope: 'present',
    },
    {
      labelKey: 'insights.chartConfigurator.periodPresetYesterday',
      id: 'yesterday',
      fromTimestamp: startOfDate(subtractFromDate(nowStart, { days: 1 }), 'day'),
      toTimestamp: subtractFromDate(nowEnd, { days: 1 }),
      scope: 'past',
    },
    {
      labelKey: 'insights.chartConfigurator.periodPresetTomorrow',
      id: 'tomorrow',
      fromTimestamp: startOfDate(addToDate(nowStart, { days: 1 }), 'day'),
      toTimestamp: addToDate(nowEnd, { days: 1 }),
      scope: 'future',
    },
    {
      labelKey: 'insights.chartConfigurator.periodPreset7D',
      id: 'prev-7-days',
      fromTimestamp: startOfDate(subtractFromDate(nowStart, { days: 6 }), 'day'),
      toTimestamp: nowEnd,
      scope: 'past',
    },
    {
      labelKey: 'insights.chartConfigurator.periodPresetN7D',
      id: 'next-7-days',
      fromTimestamp: nowStart,
      toTimestamp: startOfDate(addToDate(nowEnd, { days: 7 }), 'day'),
      scope: 'future',
    },
    {
      labelKey: 'insights.chartConfigurator.periodPreset14D',
      id: 'prev-14-days',
      fromTimestamp: startOfDate(subtractFromDate(nowStart, { days: 13 }), 'day'),
      toTimestamp: nowEnd,
      scope: 'past',
    },
    {
      labelKey: 'insights.chartConfigurator.periodPresetN14D',
      id: 'next-14-days',
      fromTimestamp: nowStart,
      toTimestamp: startOfDate(addToDate(nowEnd, { days: 14 }), 'day'),
      scope: 'future',
    },
    {
      labelKey: 'insights.chartConfigurator.periodPreset30D',
      id: 'prev-30-days',
      fromTimestamp: startOfDate(subtractFromDate(nowStart, { days: 29 }), 'day'),
      toTimestamp: nowEnd,
      scope: 'past',
    },
    {
      labelKey: 'insights.chartConfigurator.periodPresetN30D',
      id: 'next-30-days',
      fromTimestamp: nowStart,
      toTimestamp: startOfDate(addToDate(nowEnd, { days: 30 }), 'day'),
      scope: 'future',
    },
    {
      labelKey: 'insights.chartConfigurator.periodPreset90D',
      id: 'prev-90-days',
      fromTimestamp: startOfDate(subtractFromDate(nowStart, { days: 89 }), 'day'),
      toTimestamp: nowEnd,
      scope: 'past',
    },
    {
      labelKey: 'insights.chartConfigurator.periodPresetN90D',
      id: 'next-90-days',
      fromTimestamp: nowStart,
      toTimestamp: startOfDate(addToDate(nowEnd, { days: 90 }), 'day'),
      scope: 'future',
    },
    {
      labelKey: 'insights.chartConfigurator.periodPreset183D',
      id: 'prev-183-days',
      fromTimestamp: startOfDate(subtractFromDate(nowStart, { days: 182 }), 'day'),
      toTimestamp: nowEnd,
      scope: 'past',
    },
    {
      labelKey: 'insights.chartConfigurator.periodPresetN183D',
      id: 'next-183-days',
      fromTimestamp: nowStart,
      toTimestamp: startOfDate(addToDate(nowEnd, { days: 183 }), 'day'),
      scope: 'future',
    },
    {
      labelKey: 'insights.chartConfigurator.periodPreset365D',
      id: 'prev-365-days',
      fromTimestamp: startOfDate(subtractFromDate(nowStart, { days: 364 }), 'day'),
      toTimestamp: nowEnd,
      scope: 'past',
    },
    {
      labelKey: 'insights.chartConfigurator.periodPresetN365D',
      id: 'next-365-days',
      fromTimestamp: nowStart,
      toTimestamp: startOfDate(addToDate(nowEnd, { days: 365 }), 'day'),
      scope: 'future',
    },
    {
      labelKey: 'insights.chartConfigurator.periodPresetLastWeek',
      id: 'last-week',
      fromTimestamp: subtractFromDate(nowStart, { days: (getDayOfWeek(nowStart) + 6) }),
      toTimestamp: subtractFromDate(nowEnd, { days: (getDayOfWeek(nowEnd)) }),
      scope: 'past',
    },
    {
      labelKey: 'insights.chartConfigurator.periodPresetThisWeek',
      id: 'this-week',
      fromTimestamp: subtractFromDate(nowStart, { days: (getDayOfWeek(nowStart)) - 1 }),
      toTimestamp: addToDate(nowEnd, { days: (7 - getDayOfWeek(nowEnd)) }),
      scope: 'present',
    },
    {
      labelKey: 'insights.chartConfigurator.periodPresetNextWeek',
      id: 'next-week',
      fromTimestamp: addToDate(subtractFromDate(nowStart, { days: getDayOfWeek(nowStart) - 1 }), { days: 7 }),
      toTimestamp: addToDate(addToDate(nowEnd, { days: 7 - getDayOfWeek(nowEnd) }), { days: 7 }),
      scope: 'future',
    },
    {
      labelKey: 'insights.chartConfigurator.periodPresetLastMonth',
      id: 'last-month',
      fromTimestamp: startOfDate(subtractFromDate(startOfDate(nowStart, 'month'), { days: 1 }), 'month'),
      toTimestamp: endOfDate(subtractFromDate(startOfDate(nowEnd, 'month'), { days: 1 }), 'month'),
      scope: 'past',
    },
    {
      labelKey: 'insights.chartConfigurator.periodPresetThisMonth',
      id: 'this-month',
      fromTimestamp: startOfDate(nowStart, 'month'),
      toTimestamp: endOfDate(nowEnd, 'month'),
      scope: 'present',
    },
    {
      labelKey: 'insights.chartConfigurator.periodPresetNextMonth',
      id: 'next-month',
      fromTimestamp: startOfDate(addToDate(nowStart, { months: 1 }), 'month'),
      toTimestamp: endOfDate(addToDate(nowEnd, { months: 1 }), 'month'),
      scope: 'future',
    },
    {
      labelKey: 'insights.chartConfigurator.periodPresetAllTime',
      id: 'all-time',
      fromTimestamp: new Date(2000, 0, 1, 0, 0, 0),
      toTimestamp: new Date(2100, 0, 1, 0, 0, 0),
      scope: 'all',
    },
  ];

  const periodPresets = computed(() => allPeriodPresets
    .filter((preset) => scopes.includes(preset.scope))
    .map((preset) => ({
      ...preset,
      label: i18n.t(preset.labelKey) ?? '',
    })));

  /**
   * Resolves the date range from a preset and (potentially) custom range to ensure that
   * the resolved range has fromTimestamp and toTimestamp as resolved from current date.
   * Previously named applyDateRangePreset
   * @param {Object} dateRange
   * @returns {Object} The resolved date range
   */
  function resolveDateRange(dateRange) {
    const returnDateRange = {
      ...periodPresets.value.find((preset) => (preset.id === dateRange.preset)),
      preset: dateRange.preset,
    };
    delete returnDateRange.labelKey;
    if (dateRange.preset === 'custom') {
      returnDateRange.fromTimestamp = dateRange.fromTimestamp.toDate();
      returnDateRange.toTimestamp = dateRange.toTimestamp.toDate();
      returnDateRange.label = dateRange.label;
    }
    if (dateRange.periodDimension) {
      returnDateRange.periodDimension = dateRange.periodDimension;
    }
    return returnDateRange;
  } // resolveDateRange()

  /**
   * Resolve dates for a filter-compatible statement. Accepts an object of additional
   * date ranges to apply to the statement. This can be useful for eg. inherited date
   * ranges in progress reports.
   * @param {Object} statement Filter statement
   * @returns {Object} The resolved filter statement
   */
  function resolveDateRangeFilter(statement, additionalDateRanges = {}) {
    if (additionalDateRanges[statement.filterCriteriaType]) {
      const { fromTimestamp, toTimestamp } = additionalDateRanges[statement.filterCriteriaType];
      return {
        ...statement,
        fromDate: Timestamp.fromDate(fromTimestamp),
        toDate: Timestamp.fromDate(toTimestamp),
      };
    }

    const periodPreset = periodPresets.value
      .find((preset) => (preset.id === statement.filterCriteriaType));

    if (!periodPreset) {
      return statement;
    }
    return {
      ...statement,
      fromDate: Timestamp.fromDate(periodPreset.fromTimestamp),
      toDate: Timestamp.fromDate(periodPreset.toTimestamp),
    };
  } // resolveDateRangeFilter()

  /**
   *
   * @param {*} filter
   * @returns
   */
  function resolveFilterDateRanges(filter, additionalDateRanges) {
    return Object.keys(filter).reduce(
      (acc, key) => {
        if (filter[key]?.filterPropertyType === filterPropertyTypes.Date) {
          acc[key] = {
            ...filter[key],
            filterStatements: filter[key].filterStatements.map((statement) => (
              resolveDateRangeFilter(statement, additionalDateRanges)
            )),
          };
        } else {
          acc[key] = filter[key];
        }
        return acc;
      },
      {},
    );
  } // resolveFilterDateRanges()

  // periodPresets is returned as well as used internally: it is the canonical definition of ranges like
  // prev-7-days, and callers building a default table filter need it. Previously only the deprecated
  // mixins/DateRanges.js exposed it, which left `<script setup>` code with no way to reach it.
  return { periodPresets, resolveDateRange, resolveFilterDateRanges };
}
